import React, { useState } from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { useSessionStore } from '@/stores/useSessionStore';
import { useChatStore } from '@/stores/useChatStore';
import type { Session } from '@/types/session';

interface SessionItemProps {
  session: Session;
}

/**
 * 单条会话卡片。
 * 显示标题、最后活动时间，悬停显示删除按钮，当前会话高亮。
 */
const SessionItem: React.FC<SessionItemProps> = ({ session }) => {
  const currentSession = useSessionStore((s) => s.currentSession);
  const switchSession = useSessionStore((s) => s.switchSession);
  const deleteSession = useSessionStore((s) => s.deleteSession);
  const clearMessages = useChatStore((s) => s.clearMessages);
  const [hovered, setHovered] = useState(false);

  const isActive = currentSession?.session_id === session.session_id;

  const handleClick = () => {
    clearMessages();
    switchSession(session.session_id);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteSession(session.session_id);
  };

  const createdAt = format(new Date(session.created_at), 'yyyy-MM-dd HH:mm');
  const ownerName = session.user_display_name || session.user_name || '未知用户';
  const shortId = session.session_id.slice(0, 8);

  return (
    <ListItem
      disablePadding
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        mb: 0.5,
        borderRadius: 1,
        bgcolor: isActive ? 'rgba(124, 111, 92, 0.12)' : 'transparent',
        '&:hover': {
          bgcolor: isActive ? 'rgba(124, 111, 92, 0.16)' : 'rgba(124, 111, 92, 0.06)',
        },
      }}
    >
      <ListItemButton
        onClick={handleClick}
        sx={{
          borderRadius: 1,
          py: 1,
          px: 1.5,
        }}
      >
        <ListItemText
          primary={
            <Typography
              variant="body2"
              sx={{
                fontWeight: isActive ? 600 : 400,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {session.title || '新会话'}
            </Typography>
          }
          secondary={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.25 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, lineHeight: 1.35 }}>
                ID: {shortId} · {ownerName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, lineHeight: 1.35 }}>
                {createdAt}
              </Typography>
            </Box>
          }
        />

        {/* 删除按钮：悬停或当前激活时显示 */}
        {(hovered || isActive) && (
          <IconButton
            edge="end"
            size="small"
            onClick={handleDelete}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'error.main' },
              ml: 0.5,
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </ListItemButton>
    </ListItem>
  );
};

export default SessionItem;
